// @file Whiteboard AI store.
import { isAppCapableOf } from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import environment from '@@/bits/environment'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { useTldrawAi } from '@@/bits/tldraw/tldraw_hooks'
import { SearchFilters } from '@@/bits/web_search'
import { changeFrameBackground, setUseThemeFrameBackground } from '@@/bits/whiteboard'
import { logCreatedShapes } from '@@/bits/whiteboard_ai_logger'
import { openNativeWhiteboardContentPicker } from '@@/native_bridge/actions'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceAiTalkStore } from '@@/pinia/surface_ai_talk'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import type { CreateShapesPayload, WhiteboardAiAction } from '@@/surface/api/whiteboard_ai'
import { WhiteboardAiActionType, WhiteboardAiApi } from '@@/surface/api/whiteboard_ai'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { HTTPAuthenticationScheme } from '@padlet/fetch'
import { defineStore } from 'pinia'
import type { Editor, TLShape } from 'tldraw'
import { computed, ref, shallowRef } from 'vue'

const THEME_BACKGROUND_ID = 'theme_frame_background'

export const useWhiteboardAiStore = defineStore('whiteboardAi', () => {
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const contentPickerStore = useContentPickerStore()
  const surfaceContentPickerStore = useSurfaceContentPickerStore()
  const surfaceAiTalkStore = useSurfaceAiTalkStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()

  const userMessage = ref('')
  const isLoading = ref(false)
  const newlyCreatedShapes = shallowRef<TLShape[]>([])

  const xSandboxAIChat = computed(() => {
    if (!isAppUsing('sandboxAi')) return false
    // Hide the chat on mobile app
    if (device.app) return false
    // Hide the chat on small screens
    if (surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape) return false
    if (whiteboardModeStore.isReadOnlyMode) return false
    if (!whiteboardModeStore.isDrawMode) return false
    return true
  })

  function setUserMessage(message: string): void {
    userMessage.value = message.trim()
  }

  async function submitUserMessage(): Promise<void> {
    if (userMessage.value === '') return
    try {
      isLoading.value = true
      const result = await WhiteboardAiApi.classifyActions(userMessage.value, {
        authorization: [HTTPAuthenticationScheme.bearer, surfaceStartingStateStore.morpheusToken],
      })
      // If the user requests both search AI image and text-to-speech actions,
      // only perform the search AI image action to prevent multiple modals opening simultaneously
      const hasSearchAiImageRequest = result.actions.some(
        (action) => action.type === WhiteboardAiActionType.SearchAiImage,
      )
      const hasConvertTextToSpeechRequest = result.actions.some(
        (action) => action.type === WhiteboardAiActionType.ConvertTextToSpeech,
      )
      const hasRequestsForBothActions = hasSearchAiImageRequest && hasConvertTextToSpeechRequest
      const filteredActions = hasRequestsForBothActions
        ? result.actions.filter((action) => action.type !== WhiteboardAiActionType.ConvertTextToSpeech)
        : result.actions

      const promises = filteredActions.map(async (action) => await handleAction(action))
      await Promise.allSettled(promises)
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { context: 'WhiteboardAiStoreSubmitUserMessage' })
    } finally {
      isLoading.value = false
      setUserMessage('')
    }
  }

  async function handleAction(action: WhiteboardAiAction): Promise<void> {
    switch (action.type) {
      case WhiteboardAiActionType.CreateShapes: {
        await createShapes(action.payload)
        break
      }
      case WhiteboardAiActionType.SetCardBackground: {
        const payload = action.payload
        setCardBackground(payload.card_background_id)
        break
      }
      case WhiteboardAiActionType.SearchAiImage: {
        const payload = action.payload
        await openAiImageModalAndSubmitSearchQuery(payload.message)
        break
      }
      case WhiteboardAiActionType.ConvertTextToSpeech: {
        const payload = action.payload
        await openAiSpeechModalAndConvertTextToSpeech(payload.message)
        break
      }
      default:
    }
  }

  async function createShapes(payload: CreateShapesPayload): Promise<void> {
    const originalUserMessage = userMessage.value
    const { prompt } = useTldrawAi({ editor: useTldrawEditor().editor as Editor })
    if (prompt == null) return
    try {
      const { promise, cancel } = prompt({ message: asciiSafeStringify(payload), stream: true })
      await promise
      cancel()
      globalSnackbarStore.setSnackbar({
        message: 'Shape created',
        notificationType: SnackbarNotificationType.success,
      })

      // Log AI results for internal testing to help us improve the AI
      // TODO: Delete this before releasing to Beta users
      if (environment === 'production') {
        void logCreatedShapes({
          userMessage: originalUserMessage,
          shapeIds: newlyCreatedShapes.value.map((shape) => shape.id),
        })
      }
    } catch (error) {
      captureFetchException(error, { context: 'WhiteboardAiStoreCreateShape' })
    } finally {
      newlyCreatedShapes.value = []
    }
  }

  function setCardBackground(backgroundId: string): void {
    const editor = useTldrawEditor().editor as Editor
    if (editor == null) return
    const currentFrameId = editor.getCurrentPageId()
    const isUsingThemeBackground = backgroundId === THEME_BACKGROUND_ID
    const backgroundType = isUsingThemeBackground
      ? whiteboardFrameBackgroundStore.defaultFrameBackground.backgroundType
      : backgroundId
    const background = whiteboardFrameBackgroundStore.backgroundTypeToBackground[backgroundType]
    if (background == null) return
    setUseThemeFrameBackground(currentFrameId, isUsingThemeBackground)
    changeFrameBackground(currentFrameId, background.id, background.url)
    globalSnackbarStore.setSnackbar({
      message: 'Card background updated',
      notificationType: SnackbarNotificationType.success,
    })
  }

  async function openAiImageModalAndSubmitSearchQuery(query: string): Promise<void> {
    try {
      if (device.app && isAppCapableOf('nativeWhiteboardContentPicker')) {
        postMessage(openNativeWhiteboardContentPicker())
        return
      }
      surfaceContentPickerStore.showPostContentPicker()
      contentPickerStore.showContentSourceMenu({ contentPickerContext: 'whiteboardUpload' })
      contentPickerStore.openAiImagePreview()
      contentPickerStore.setSearchQuery({ query })
      contentPickerStore.pickSearchFilter({ filter: SearchFilters.AiImages })
      await contentPickerStore.search({ query })
    } catch (error) {
      captureFetchException(error, { context: 'WhiteboardAiStoreOpenAiImageModalAndSubmitSearchQuery' })
    }
  }

  async function openAiSpeechModalAndConvertTextToSpeech(query: string): Promise<void> {
    try {
      if (device.app && isAppCapableOf('nativeWhiteboardContentPicker')) {
        postMessage(openNativeWhiteboardContentPicker())
        return
      }
      surfaceContentPickerStore.showPostContentPicker()
      contentPickerStore.showContentSourceMenu({ contentPickerContext: 'whiteboardUpload' })
      contentPickerStore.pickContentSource({ source: ContentPickerSource.AiTalk })
      surfaceAiTalkStore.text = query
      await surfaceAiTalkStore.generateAudio()
    } catch (error) {
      captureFetchException(error, { context: 'WhiteboardAiStoreOpenAiSpeechModalAndConvertTextToSpeech' })
    }
  }

  async function transcribeAudio(audioFile: File): Promise<void> {
    try {
      isLoading.value = true
      const transcription = await WhiteboardAiApi.transcribeAudio(audioFile, {
        authorization: [HTTPAuthenticationScheme.bearer, surfaceStartingStateStore.morpheusToken],
      })

      if (transcription != null && transcription !== '') {
        const message = userMessage.value !== '' ? userMessage.value + ' ' + transcription.trim() : transcription
        setUserMessage(message)
      } else {
        globalSnackbarStore.setSnackbar({
          message: __('Sorry, we didn’t understand your request'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    } catch (error) {
      isLoading.value = false
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { context: 'WhiteboardAiStoreTranscribeAudio' })
    }
  }

  return {
    userMessage,
    isLoading,
    newlyCreatedShapes,
    xSandboxAIChat,

    setUserMessage,
    submitUserMessage,
    transcribeAudio,
  }
})
